import moment from 'moment';
import React from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import { parseDate } from 'react-day-picker/moment';
import { checkValidDateFormat } from '../validators';

const currentYear = new Date().getFullYear();
const fromMonth = new Date(currentYear - 100, 0);
const toMonth = new Date(currentYear + 10, 11);
const weekdayMappings = {
  Sundays: 0,
  Mondays: 1,
  Tuesdays: 2,
  Wednesdays: 3,
  Thursdays: 4,
  Fridays: 5,
  Saturdays: 6,
};

function YearMonthForm({ date, localeUtils, onChange }) {
  const months = localeUtils.getMonths();

  const years = [];
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i);
  }

  const handleChange = function handleChange(e) {
    const { year, month } = e.target.form;
    onChange(new Date(year.value, month.value));
  };

  return (
    <div className='DayPicker-Caption'>
      <select name='month' onChange={handleChange} value={date.getMonth()} style={{ width: '50%' }}>
        {months.map((month, i) => (
          <option key={month} value={i}>
            {month}
          </option>
        ))}
      </select>
      <select
        name='year'
        onChange={handleChange}
        value={date.getFullYear()}
        style={{ width: '30%' }}
      >
        {years.map(year => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
}

const isInvalidDateInstance = val => val instanceof Date && isNaN(val);

const shouldFlipFromBounds = (inputBounds, formBounds) => {
  const heightAboveInput = inputBounds.top - formBounds.top;
  const heightBelowInput = formBounds.bottom - inputBounds.bottom;

  const enoughHeightAboveToFlip = heightAboveInput > DATE_PICKER_RENDER_HEIGHT;
  const shouldFlip = heightBelowInput < DATE_PICKER_RENDER_HEIGHT;

  return shouldFlip && enoughHeightAboveToFlip;
};

const shouldFlip = (input) => {
  if (!input || !input.form) {
    return false;
  }

  const form = input.form;

  if (!input.getBoundingClientRect || !form.getBoundingClientRect) {
    return false;
  }

  const inputBounds = input.getBoundingClientRect();
  const formBounds = form.getBoundingClientRect();

  return shouldFlipFromBounds(inputBounds, formBounds);
};

const DATE_PICKER_RENDER_HEIGHT = 300; // px
function CustomOverlay(allProps) {
  const { classNames, children, ...props } = allProps;

  const overlayStyle = {};

  if (shouldFlip(allProps.input)) {
    overlayStyle.transform = 'translate(0, calc(-100% - 40px))';
  }

  return (
    <div
      className={classNames.overlayWrapper}
      {...props}
    >
      <div className={classNames.overlay}
        style={overlayStyle}>
        {children}
      </div>
    </div>
  );
}

export default class FormFieldInputDate extends React.Component {
  constructor(props) {
    super(props);
    this.dayPickerRef = React.createRef()
    this.state = {
      month: new Date(),
      selectedDay: undefined,
      isValidDay: true,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value && nextProps.value !== this.props.value) {
      this.setState(prev => ({ ...prev, selectedDay: nextProps.value }))
    }
  }

  formatDate = date => {
    const format = this.props.placeholder.toUpperCase();
    const isValidFormat = checkValidDateFormat(date, { dateFormat: format }).valid;
    if (this.state.isValidDay && isValidFormat) {
      return moment(date).format(format);
    }
    return this.state.selectedDay;
  };

  getDayPickerProps = () => {
    const { minDate, maxDate, disabledWeekdays } = this.props;
    let daysOfWeek;
    const pickerProps = {
      // props for handling month/year selection
      month: this.state.month,
      fromMonth,
      toMonth,
      onBlur: (event) => {
        // ISSUE: https://github.com/gpbl/react-day-picker/issues/600
        // onBlur is triggered when user click Next/Previous Month
        // Solution: check only hide DayPicker if related targets do not belong to DayPicker 

        const relatedTarget = event.relatedTarget;

        if (!(relatedTarget && (relatedTarget.classList.contains('DayPicker-NavButton') ||
          relatedTarget.classList.contains('DayPicker-wrapper') ||
          ['year', 'month'].includes(relatedTarget.getAttribute('name'))
        ))) {
          setTimeout(() => {
            this.dayPickerRef.current && this.dayPickerRef.current.hideDayPicker()
          }, 500)
        }
      },
      captionElement: ({ date, localeUtils }) => (
        <YearMonthForm
          date={date}
          localeUtils={localeUtils}
          onChange={this.handleYearMonthChange}
        />
      ),
      modifiers: {},
    };

    const before = { before: minDate || fromMonth };
    const after = { after: maxDate || toMonth };
    if (disabledWeekdays)
      daysOfWeek = {
        daysOfWeek: disabledWeekdays.map(weekday => weekdayMappings[weekday[0]]),
      };

    pickerProps.modifiers.disabled = [before, after, daysOfWeek];

    return pickerProps;
  };

  handleYearMonthChange = month => this.setState({ month });

  handleDayChange = (day, modifiers, dayPickerInput) => {
    const isValidDay = typeof day !== 'undefined';
    this.setState({
      isValidDay,
      selectedDay: isValidDay ? day : dayPickerInput.input.value,
    });
    if (day) this.props.onChange(day);
  };

  handleBlur = ({ target, relatedTarget }) => {
    const { isValidDay, selectedDay } = this.state;
    const dateVal = isValidDay ? selectedDay : target.value;

    // don't call onChange if this blur event was triggered by user
    // selecting either the month or year dropdowns of the date picker component

    if (!relatedTarget || !['year', 'month'].includes(relatedTarget.name)) {
      this.props.onChange(dateVal);
    }

  };

  handleKeyPress = e => {
    if (!/\d|\/|\-|\./.test(e.key)) e.preventDefault();
  };

  render() {
    const {
      className,
      placeholder,
      sublabel,
      id,
      onBlur,
      value,
      disabled,
      readOnly,
    } = this.props;

    const { classNames } = DayPickerInput.defaultProps;
    return (
      <div className={`${className} date-picker`}>
        <div>
          <DayPickerInput
            classNames={{ ...classNames, container: `${classNames.container} full-width` }}
            placeholder={placeholder.toUpperCase()}
            format={placeholder.toUpperCase()}
            ref={this.dayPickerRef}
            formatDate={this.formatDate}
            parseDate={parseDate}
            dayPickerProps={this.getDayPickerProps()}
            onDayChange={this.handleDayChange}
            onDayPickerHide={onBlur}
            value={value ? new Date(moment(value).format()) : ''}
            keepFocus={false}
            overlayComponent={CustomOverlay}
            inputProps={{
              id,
              disabled,
              readOnly,
              type: 'text',
              onKeyPress: this.handleKeyPress,
              onBlur: this.handleBlur,
            }}
          />
          {sublabel && <div className='form-field-label secondary'>{sublabel}</div>}
        </div>
      </div>
    );
  }
}

FormFieldInputDate.defaultProps = {
  className: '',
  sublabel: '',
};
